/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Stack, Heading, Divider} from '@salesforce/retail-react-app/app/components/shared/ui'

import CartItems from './cart-items'
import PriceBreakdown from './price-breakdown'
import TotalSection from './total-section'
import AppliedPromos from './applied-promos'
import {SPACING, DIVIDER_STYLES, COMPONENT_IDS, TEST_IDS} from './constants'
import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'

const OrderSummary = ({basket, showCartItems = false, isEstimate = false, fontSize = 'md'}) => {
    const {tCart} = useTranslationHelpers()
    if (!basket?.basketId && !basket?.orderNo) {
        return null
    }

    return (
        <Stack data-testid={TEST_IDS.orderSummary} spacing={SPACING.main}>
            {/* Header with "Summary" title */}
            <Heading fontSize="xl" pt={1} id={COMPONENT_IDS.orderSummaryHeading} color="gray.500">
                {tCart?.summaryTitle}
            </Heading>

            <Stack
                spacing={SPACING.section}
                align="flex-start"
                role="region"
                aria-labelledby={COMPONENT_IDS.orderSummaryHeading}
            >
                {/* Cart items list with product details (optional) */}
                {showCartItems && <CartItems basket={basket} />}

                <Divider {...DIVIDER_STYLES.light} />

                {/* Price breakdown: Subtotal, Shipping, Tax */}
                <PriceBreakdown basket={basket} fontSize={fontSize} />

                <Divider {...DIVIDER_STYLES.heavy} />

                <Stack spacing="22px" w="full">
                    {/* Final total with tax info */}
                    <TotalSection basket={basket} isEstimate={isEstimate} />
                </Stack>
            </Stack>
        </Stack>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool,
    fontSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl'])
}

export default OrderSummary
